import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DatePicker, Radio, Select, Spin } from 'antd';
import moment from 'moment';
import { useParams, Link } from 'react-router-dom';
import { LoadingOutlined, RightOutlined } from '@ant-design/icons';
import './styles/reports.scss';
import {
  fetchReports,
  getMonth,
  setMonth,
  getReports,
  getReportsLoading,
  getYear,
  setYear,
  getTenant,
  setTenant,
  getSite,
  setSite,
  initiate
} from '../../../features/reports.store';
import SubPageLayout from '../helpers/SubPageLayout';
import { format } from 'date-fns';
import { DATE_FORMAT } from '../../../utils/constant';
import { downloadHandoverPdf, downloadPdf } from '../../../utils/report-download';
import SearchFilterInput from '../helpers/SearchFilterInput';
import { getIsAdminApi, getUser } from '../../../features/auth.store';
import { ROLE } from '../../../utils/features';
import get from 'lodash.get';
import { GET } from '../../../service/base-api-service';

const parseDateFromFileName = (fileName = '') => {
  const patterns = [
    /(\d{4})[-_.](\d{2})[-_.](\d{2})/,
    /(\d{2})[-_.](\d{2})[-_.](\d{4})/,
  ];

  for (const pattern of patterns) {
    const match = String(fileName).match(pattern);
    if (!match) continue;

    const isYearFirst = match[1]?.length === 4;
    const year = isYearFirst ? match[1] : match[3];
    const month = match[2];
    const day = isYearFirst ? match[3] : match[1];
    const parsed = new Date(`${year}-${month}-${day}T00:00:00`);
    if (!Number.isNaN(parsed.getTime())) return parsed;
  }

  return null;
};

const getReportDate = (report = {}, type) => {
  if (type !== 'handover') {
    const parsed = new Date(report.servicedDate);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  const candidates = [
    report.servicedDate,
    report.uploadedAt,
    report.createdAt,
    report.createdDate,
    report.lastModified,
    report.serviceDate,
    report.date,
  ].filter(Boolean);

  for (const candidate of candidates) {
    const parsed = new Date(candidate);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }
  }

  return parseDateFromFileName(report.fileName || report.name || '');
};

const ReportTile = ({ report, type, tenantId, siteId, handoverLabel }) => {
  const [loading, setLoading] = useState(false);
  const isAdmin = useSelector(getIsAdminApi);

  const serviceFormId = report?.serviceFormId;
  const chamberName = type === 'handover' ? handoverLabel : report?.chamberName;
  const siteName = type === 'handover' ? report?.siteName || report?.site || report?.siteId || siteId : report?.siteName;
  const servicedDate = getReportDate(report, type);

  const onDownloadPdf = async () => {
    setLoading(true);
    if (type === 'serviced') {
      await downloadPdf(serviceFormId, isAdmin, tenantId);
    }
    if (type === 'handover') {
      await downloadHandoverPdf({
        tenantId,
        siteId,
        isAdmin,
        fileName: report?.fileName || report?.name || `handover-${Date.now()}.pdf`,
      });
    }
    setLoading(false);
  };

  const downloadButton = loading ? (
    <LoadingOutlined />
  ) : (
    <img src="/icons/download.svg" onClick={onDownloadPdf} />
  );

  return (
    <div className={`report-tile d-flex rounded-border bg-white ai-center ${type === 'handover' ? 'handover-report' : 'jc-space-between'}`}>
      {(type === 'serviced' || type === 'handover') && <>{downloadButton}</>}

      {type === 'handover' ? (
        <div className="handover-content d-flex f-col">
          <div className="name">{chamberName}</div>
          <div className="handover-meta d-flex ai-center">
            <div className="meta-item">{siteName || '-'}</div>
            <div className="meta-item">{servicedDate ? format(new Date(servicedDate), DATE_FORMAT) : '-'}</div>
          </div>
        </div>
      ) : (
        <>
          <div className="name">{chamberName}</div>
          <div className="date">{siteName}</div>
          <div className="date">{servicedDate ? format(new Date(servicedDate), DATE_FORMAT) : '-'}</div>
        </>
      )}

      {type === 'after-service' && (
        <Link to={`/reports/after-service/${serviceFormId}?tenantId=${tenantId}`}>
          <RightOutlined />
        </Link>
      )}
    </div>
  );
};

const dateRangeFilter = [
  { label: 'Jan-Apr', value: 'j-a' },
  { label: 'May-Aug', value: 'm-a' },
  { label: 'Sep-Dec', value: 's-d' },
];

const normalizeRoles = (user) => {
  const scope = get(user, 'scope');
  const role = get(user, 'role');
  const roles = get(user, 'roles', []);
  const authorities = get(user, 'authorities', []);
  const authorityNames = Array.isArray(authorities)
    ? authorities.map((a) => (typeof a === 'string' ? a : a?.authority)).filter(Boolean)
    : [];

  const splitScope =
    typeof scope === 'string' ? scope.split(',').map((s) => s.trim()).filter(Boolean) : [];

  return Array.from(
    new Set([
      ...splitScope,
      ...(role ? [role] : []),
      ...(Array.isArray(roles) ? roles : []),
      ...authorityNames,
    ].filter(Boolean)),
  );
};

const hasRole = (user, roleConst) => normalizeRoles(user).includes(roleConst);

const getTenantIdentifierFromUser = (user) =>
  get(user, 'tenant.identifier') ||
  get(user, 'tenant.id') ||
  get(user, 'tenantIdentifier') ||
  get(user, 'tenant_identifier') ||
  get(user, 'tenantId') ||
  '';

const getTenantNameFromUser = (user) =>
  get(user, 'tenant.name') ||
  get(user, 'tenantName') ||
  get(user, 'tenant_name') ||
  get(user, 'companyName') ||
  '';

const toSiteId = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? String(value) : numeric;
};

const getAssignedSiteIdsFromUser = (user) => {
  const assignedSites = get(user, 'assignedSites', []);
  if (Array.isArray(assignedSites) && assignedSites.length > 0) {
    return Array.from(
      new Set(
        assignedSites
          .map((site) => toSiteId(get(site, 'id')))
          .filter((siteId) => siteId !== null),
      ),
    );
  }

  const assignedSiteIds = get(user, 'assignedSiteIds', []);
  if (Array.isArray(assignedSiteIds) && assignedSiteIds.length > 0) {
    return Array.from(new Set(assignedSiteIds.map((siteId) => toSiteId(siteId)).filter((siteId) => siteId !== null)));
  }

  const userSites = get(user, 'user_sites');
  if (typeof userSites === 'string' && userSites.trim()) {
    return Array.from(
      new Set(
        userSites
          .split(',')
          .map((siteId) => toSiteId(siteId.trim()))
          .filter((siteId) => siteId !== null),
      ),
    );
  }

  const singleSiteId = toSiteId(get(user, 'site.id') || get(user, 'site_id') || get(user, 'siteId'));
  return singleSiteId === null ? [] : [singleSiteId];
};

const getAssignedSitesFromUser = (user) => {
  const assignedSites = get(user, 'assignedSites', []);
  if (Array.isArray(assignedSites) && assignedSites.length > 0) {
    return assignedSites
      .map((site) => {
        const id = toSiteId(get(site, 'id'));
        const name = get(site, 'name') || get(site, 'siteName') || get(site, 'label') || (id !== null ? String(id) : '');
        return id !== null ? { id, name } : null;
      })
      .filter(Boolean);
  }

  const singleSiteId = toSiteId(get(user, 'site.id') || get(user, 'site_id') || get(user, 'siteId'));
  const singleSiteName = get(user, 'site.name') || get(user, 'site_name') || '';

  if (singleSiteId !== null) {
    return [{ id: singleSiteId, name: singleSiteName || String(singleSiteId) }];
  }

  const assignedSiteIds = getAssignedSiteIdsFromUser(user);
  return assignedSiteIds.map((id) => ({ id, name: String(id) }));
};

const normalizeId = (value) => String(value);

const getReportSiteId = (report) =>
  get(report, 'site.id') ||
  get(report, 'siteId') ||
  get(report, 'site_id') ||
  get(report, 'siteIdentifier') ||
  null;

const getReportSiteName = (report) =>
  get(report, 'site.name') ||
  get(report, 'siteName') ||
  get(report, 'site_name') ||
  '';

const normalizeList = (payload) => {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.results)) return payload.results;
  if (Array.isArray(payload?.list)) return payload.list;
  return payload ? [payload] : [];
};

const getSiteIdFromRecord = (site) =>
  toSiteId(
    get(site, 'id') ||
      get(site, 'siteId') ||
      get(site, 'site_id') ||
      get(site, 'identifier') ||
      null,
  );

const getSiteNameFromRecord = (site) =>
  get(site, 'name') ||
  get(site, 'siteName') ||
  get(site, 'site_name') ||
  get(site, 'displayName') ||
  '';

const Reports = () => {
  const { type } = useParams();
  const reports = useSelector(getReports)(type);
  const selectedYear = useSelector(getYear)(type);
  const selectedMonth = useSelector(getMonth)(type);
  const selectedTenant =  useSelector(getTenant)(type);
  const selectedSite = useSelector(getSite)(type);
  const isLoading = useSelector(getReportsLoading);
  const isHandover = type === 'handover';
  const user = useSelector(getUser);
  const { RangePicker } = DatePicker;
  const [handoverDateRange, setHandoverDateRange] = useState([]);
  const [siteOptionsForRole, setSiteOptionsForRole] = useState([]);

  const isAdminType = useSelector(getIsAdminApi);
  const isSiteRole = useMemo(() => hasRole(user, ROLE.SU), [user]);
  const isTenantRole = useMemo(() => hasRole(user, ROLE.TA), [user]);
  const userTenantIdentifier = useMemo(() => getTenantIdentifierFromUser(user), [user]);
  const userTenantName = useMemo(() => getTenantNameFromUser(user), [user]);
  const assignedSites = useMemo(() => getAssignedSitesFromUser(user), [user]);
  const defaultAssignedSiteOptions = useMemo(
    () => assignedSites.map((site) => ({ value: String(site.id), label: site.name || String(site.id) })),
    [assignedSites],
  );
  const assignedSiteOptions = isSiteRole ? siteOptionsForRole : defaultAssignedSiteOptions;
  const selectedAssignedSiteLabel = useMemo(() => {
    const found = assignedSiteOptions.find((site) => String(site.value) === String(selectedSite));
    return found?.label || '';
  }, [assignedSiteOptions, selectedSite]);

  const dispatch = useDispatch();

  const changeYear = (value) => {
    dispatch(setYear({type, value: value.year().toString()}));
  };

  const changeTenant = (value) => {
    if (isHandover) {
      dispatch(setSite({ type, value: '' }));
    }

    if(selectedTenant == ''){
      dispatch(initiate(value));
    }else{
      dispatch(setTenant({type, value}));
    }
    
  };

  useEffect(() => {
    let cancelled = false;

    const buildFallbackOptions = () => {
      const fallback = defaultAssignedSiteOptions;
      if (!cancelled) {
        setSiteOptionsForRole(fallback);
      }
      return fallback;
    };

    const loadSiteOptions = async () => {
      if (!isSiteRole) {
        setSiteOptionsForRole([]);
        return;
      }

      const fallback = buildFallbackOptions();
      const assignedIdSet = new Set(getAssignedSiteIdsFromUser(user).map((id) => normalizeId(id)));
      const endpoints = ['v1/site', 'v1/site/all'];

      for (const endpoint of endpoints) {
        try {
          const response = await GET(endpoint);
          const rows = normalizeList(response);
          if (!rows.length) continue;

          let options = rows
            .map((site) => {
              const siteId = getSiteIdFromRecord(site);
              if (siteId === null) return null;
              const siteName = getSiteNameFromRecord(site);
              return {
                value: String(siteId),
                label: siteName || String(siteId),
              };
            })
            .filter(Boolean);

          if (assignedIdSet.size > 0) {
            options = options.filter((opt) => assignedIdSet.has(normalizeId(opt.value)));
          }

          if (options.length > 0) {
            const deduped = Array.from(new Map(options.map((opt) => [opt.value, opt])).values());
            if (!cancelled) {
              setSiteOptionsForRole(deduped);
            }
            return;
          }
        } catch (err) {
          continue;
        }
      }

      if (!cancelled) {
        setSiteOptionsForRole(fallback);
      }
    };

    loadSiteOptions();

    return () => {
      cancelled = true;
    };
  }, [isSiteRole, user, defaultAssignedSiteOptions]);

  useEffect(() => {
    if ((isSiteRole || isTenantRole) && userTenantIdentifier && !selectedTenant) {
      dispatch(initiate(userTenantIdentifier));
    }
  }, [dispatch, isSiteRole, isTenantRole, userTenantIdentifier, selectedTenant]);

  useEffect(() => {
    if (!isSiteRole) return;
    if (selectedSite) return;

    const firstAssigned = assignedSiteOptions[0]?.value;
    if (firstAssigned !== undefined && firstAssigned !== null) {
      dispatch(setSite({ type, value: String(firstAssigned) }));
    }
  }, [dispatch, type, isSiteRole, selectedSite, assignedSiteOptions]);

  useEffect(() => {
    const hasRequiredFilters = isHandover
      ? selectedTenant && (!isSiteRole || selectedSite)
      : (!isAdminType || selectedTenant);

    if (hasRequiredFilters) {
      if (isHandover) {
        dispatch(fetchReports({
          type,
          tenantId: selectedTenant,
          siteId: isSiteRole ? selectedSite : selectedSite || undefined,
          month: selectedMonth || 'j-a',
        }));
        return;
      }

      dispatch(fetchReports({ month: selectedMonth || 'j-a', type, company: selectedTenant }));
    }
  }, [selectedYear, type, selectedTenant, selectedSite, selectedMonth]);

  const onMonthChange = (e) => {
    dispatch(setMonth({type, value: e.target.value}))

    if (isHandover && (!selectedTenant || (isSiteRole && !selectedSite))) {
      return;
    }

    if (isHandover) {
      dispatch(fetchReports({
        type,
        tenantId: selectedTenant,
        siteId: isSiteRole ? selectedSite : selectedSite || undefined,
        month: e.target.value,
      }));
      return;
    }

    dispatch(fetchReports({ month: e.target.value, type, company: selectedTenant }));
  };

  const filteredReports = useMemo(() => {
    if (!isHandover) {
      if (isSiteRole && selectedSite) {
        const selectedSiteKey = normalizeId(selectedSite);
        const selectedSiteName = String(selectedAssignedSiteLabel || '').toLowerCase();

        const scoped = reports.filter((item) => {
          const reportSiteId = getReportSiteId(item);
          if (reportSiteId !== null && reportSiteId !== undefined && normalizeId(reportSiteId) === selectedSiteKey) {
            return true;
          }

          const reportSiteName = String(getReportSiteName(item) || '').toLowerCase();
          return Boolean(selectedSiteName && reportSiteName && reportSiteName === selectedSiteName);
        });

        // Do not hide all reports when payload has inconsistent site fields.
        return scoped.length > 0 ? scoped : reports;
      }

      return reports;
    }

    const [startDate, endDate] = handoverDateRange || [];
    if (!startDate || !endDate) {
      return reports;
    }

    const start = moment(startDate).startOf('day');
    const end = moment(endDate).endOf('day');

    return reports.filter((item) => {
      const date = getReportDate(item, 'handover');
      if (!date) {
        return false;
      }

      const value = moment(date);
      return value.isBetween(start, end, null, '[]');
    });
  }, [reports, handoverDateRange, isHandover, isSiteRole, selectedSite, selectedAssignedSiteLabel]);

  const reportsWithLabels = useMemo(() => {
    if (!isHandover) {
      return filteredReports.map((report) => ({ report, handoverLabel: '' }));
    }

    const dayCounter = {};

    return filteredReports.map((report) => {
      const reportDate = getReportDate(report, 'handover');
      const dateKey = reportDate ? format(new Date(reportDate), 'yyyy-MM-dd') : 'no-date';
      dayCounter[dateKey] = (dayCounter[dateKey] || 0) + 1;

      return {
        report,
        handoverLabel: `Handover ${dayCounter[dateKey]}`,
      };
    });
  }, [filteredReports, isHandover]);

  const subtitle = type === 'serviced' ? 'Serviced' : type === 'after-service' ? 'After Service' : 'Handover Reports';

  return (
    <SubPageLayout title="Reports" subtitle={subtitle}>
      <div className="reports-filter-bar d-flex ai-center">
        {isSiteRole ? (
          <div className="reports-filter-field">
            <Select
              className="w-100"
              placeholder="Select Site"
              value={selectedSite || undefined}
              onChange={(value) => dispatch(setSite({ type, value }))}
              options={assignedSiteOptions}
            />
          </div>
        ) : isTenantRole ? (
          <div className="reports-filter-field">
            <Select
              className="w-100"
              value={selectedTenant || undefined}
              onChange={changeTenant}
              options={
                userTenantIdentifier
                  ? [{ value: userTenantIdentifier, label: userTenantName || userTenantIdentifier }]
                  : []
              }
            />
          </div>
        ) : (
          <SearchFilterInput
            placeholder="Search."
            companyDropdown
            selectFirstIfValueIsEmpty
            companyProps={{
              onChange: changeTenant,
              selectFirstIfValueIsEmpty: true,
              placeholder: 'Select Company',
              value: selectedTenant,
            }}
            
          />
        )}

        {isHandover && !isSiteRole && (
          <SearchFilterInput
            sitesDropdown
            siteProps={{
              onChange: (value) => dispatch(setSite({ type, value })),
              selectFirstIfValueIsEmpty: true,
              placeholder: 'Select Site',
              company: selectedTenant,
              value: selectedSite,
            }}
          />
        )}

        <div className="reports-filter-field">
          <DatePicker value={moment(selectedYear)} picker="year" className="w-100" onChange={changeYear} />
        </div>

        {isHandover && (
          <div className="reports-filter-field">
            <RangePicker
              className="w-100"
              value={handoverDateRange}
              onChange={(value) => setHandoverDateRange(value || [])}
            />
          </div>
        )}
      </div>
      <div className="reports poppins">
        <Radio.Group
          options={dateRangeFilter}
          onChange={onMonthChange}
          value={selectedMonth}
          optionType="button"
          className="reports-date-range-selector"
        />

        <Spin spinning={isLoading}>
          <div className="tile-container">
            {reportsWithLabels.map(({ report, handoverLabel }, index) => (
              <ReportTile
                key={report.serviceFormId || report.fileName || `handover-${index}`}
                report={report}
                type={type}
                tenantId={selectedTenant}
                siteId={selectedSite}
                handoverLabel={handoverLabel}
              />
            ))}
          </div>
        </Spin>
      </div>
    </SubPageLayout>
  );
};

export default Reports;
